import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Plus,
  Trash2,
  Edit,
  LogOut,
  Car,
  Calendar,
  Gauge,
  Fuel,
  Cog,
  Loader2,
  Eye,
  User,
  Camera,
  FileText,
} from "lucide-react";
import { useSiteMeta } from "./hooks/useSiteMeta";
import {
  DepotVenteListingForm,
  EMPTY_LISTING_FORM,
  listingToForm,
  buildTitle,
  buildKmLabel,
  buildImageUrl,
} from "./components/depotvente/DepotVenteListingForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "depot_vente_tech_token";
const NAME_KEY = "depot_vente_tech_name";

const STATUS_LABELS = {
  disponible: "Disponible",
  reserve: "Réservé",
  vendu: "Vendu",
};

const StatusPill = ({ status }) => {
  const color =
    status === "vendu"
      ? "bg-red-500/20 text-red-300 border-red-500/30"
      : status === "reserve"
      ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/30"
      : "bg-green-500/20 text-green-300 border-green-500/30";
  return (
    <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded border ${color}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const DepotVenteTech = () => {
  useSiteMeta({ title: "Espace Dépôt-Vente — ATR", description: "Interface technicien pour gérer les véhicules en dépôt-vente.", noindex: true });

  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [techName, setTechName] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [loginForm, setLoginForm] = useState({ name: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_LISTING_FORM);
  const [saving, setSaving] = useState(false);

  const fetchListings = useCallback(async (authToken) => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/depot-vente-tech/listings?token=${authToken}`);
      setListings(res.data.listings || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expirée — reconnexion nécessaire");
        setToken("");
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(NAME_KEY);
      } else {
        toast.error("Erreur au chargement des annonces");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchListings(token);
  }, [token, fetchListings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.name.trim() || !loginForm.password) {
      toast.error("Nom et mot de passe requis");
      return;
    }
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/depot-vente-tech/login`, loginForm);
      const t = res.data.token;
      const n = res.data.name;
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(NAME_KEY, n);
      setToken(t);
      setTechName(n);
      toast.success(`Bienvenue ${n}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_KEY);
    setToken("");
    setTechName("");
  };

  const startNew = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    setForm({ ...EMPTY_LISTING_FORM, deposit_date: `${dd}/${mm}/${yyyy}` });
    setEditingId("new");
  };

  const startEdit = (listing) => {
    setForm(listingToForm(listing));
    setEditingId(listing.id);
  };

  const cancelEdit = () => {
    setForm(EMPTY_LISTING_FORM);
    setEditingId(null);
  };

  const saveListing = async () => {
    const title = buildTitle(form);
    if (!title) {
      toast.error("Titre requis (ou marque + modèle)");
      return;
    }
    const km = buildKmLabel(form);
    setSaving(true);
    const payload = { ...form, title, km };
    try {
      if (editingId === "new") {
        await axios.post(`${API}/depot-vente-tech/listing?token=${token}`, payload);
        toast.success("Fiche véhicule créée");
      } else {
        await axios.put(`${API}/depot-vente-tech/listing/${editingId}?token=${token}`, payload);
        toast.success("Fiche véhicule mise à jour");
      }
      await fetchListings(token);
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur à l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteListing = async (listing) => {
    if (!window.confirm(`Supprimer définitivement "${listing.title}" ?`)) return;
    try {
      await axios.delete(`${API}/depot-vente-tech/listing/${listing.id}?token=${token}`);
      toast.success("Fiche supprimée");
      await fetchListings(token);
    } catch (err) {
      toast.error("Erreur à la suppression");
    }
  };

  // ============== RENDER ==============
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4" data-testid="dv-tech-login">
        <Toaster position="top-right" richColors />
        <div className="w-full max-w-md bg-[#121212] border border-white/[0.06] rounded-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#FF3B30]/[0.15] flex items-center justify-center">
              <Car size={20} className="text-[#FF3B30]" />
            </div>
            <div>
              <h1 className="text-white font-bold text-xl">Espace Dépôt-Vente</h1>
              <p className="text-white/50 text-xs">Réservé aux techniciens ATR</p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Nom du technicien</label>
              <input
                type="text"
                data-testid="dv-tech-login-name"
                value={loginForm.name}
                onChange={(e) => setLoginForm({ ...loginForm, name: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30]"
                placeholder="Ex : Amine"
                required
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Mot de passe dépôt-vente</label>
              <input
                type="password"
                data-testid="dv-tech-login-password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30]"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="dv-tech-login-btn"
              className="w-full bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold py-3 rounded-md transition-colors uppercase tracking-wider text-sm flex items-center justify-center gap-2"
            >
              {loggingIn ? <Loader2 size={16} className="animate-spin" /> : null}
              {loggingIn ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <p className="mt-6 text-white/40 text-xs text-center">
            Mot de passe fourni par l&apos;admin. Contacte-le en cas d&apos;oubli.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pb-16" data-testid="dv-tech-app">
      <Toaster position="top-right" richColors />

      <div className="bg-[#121212] border-b border-white/[0.06] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF3B30]/[0.15] flex items-center justify-center">
              <Car size={18} className="text-[#FF3B30]" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">Espace Dépôt-Vente</h1>
              <p className="text-white/50 text-xs">Connecté en tant que <span className="text-white/80 font-semibold">{techName}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/achat-vente-voiture-clermont-ferrand"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-white/60 hover:text-white text-xs font-medium px-3 py-2 rounded-md border border-white/[0.06] hover:border-white/[0.12] transition-colors"
            >
              <Eye size={13} /> Voir page publique
            </a>
            <button
              onClick={handleLogout}
              data-testid="dv-tech-logout-btn"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-red-400 text-xs font-medium px-3 py-2 rounded-md border border-white/[0.06] hover:border-red-400/30 transition-colors"
            >
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {editingId ? (
          <DepotVenteListingForm
            form={form}
            setForm={setForm}
            saving={saving}
            onCancel={cancelEdit}
            onSave={saveListing}
            isNew={editingId === "new"}
            testIdPrefix="dv-tech-form"
            uploadEndpoint={`/api/depot-vente-tech/upload-image?token=${token}`}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white font-bold text-xl">Véhicules en dépôt-vente ({listings.length})</h2>
              <button
                onClick={startNew}
                data-testid="dv-tech-add-btn"
                className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-4 py-2.5 rounded-md transition-colors uppercase tracking-wider text-sm"
              >
                <Plus size={16} />
                Nouvelle fiche véhicule
              </button>
            </div>

            {loading ? (
              <div className="text-center py-16 text-white/40">
                <Loader2 size={24} className="animate-spin mx-auto mb-2" />
                Chargement...
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-16 bg-[#121212] border border-white/[0.06] rounded-xl">
                <Car size={40} className="text-white/20 mx-auto mb-3" />
                <p className="text-white/60 mb-4">Aucun véhicule en dépôt-vente pour le moment</p>
                <button
                  onClick={startNew}
                  className="inline-flex items-center gap-2 text-[#FF3B30] hover:text-[#ff6b62] font-semibold text-sm"
                >
                  <Plus size={14} /> Créer la première fiche véhicule
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {listings.map((v) => (
                  <ListingCard
                    key={v.id}
                    v={v}
                    onEdit={() => startEdit(v)}
                    onDelete={() => deleteListing(v)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ListingCard = ({ v, onEdit, onDelete }) => {
  const cover = v.image_url || (Array.isArray(v.images) && v.images[0]) || "";
  const photosCount = (v.images?.length || 0) + (v.image_url ? 1 : 0);
  return (
    <article
      data-testid={`dv-tech-listing-${v.id}`}
      className="bg-[#121212] border border-white/[0.06] rounded-lg overflow-hidden hover:border-[#FF3B30]/30 transition-colors flex flex-col sm:flex-row"
    >
      <div className="sm:w-56 h-40 sm:h-auto bg-[#1a1a1a] flex-shrink-0 relative">
        {cover ? (
          <img src={buildImageUrl(cover)} alt={v.title} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Car size={40} className="text-white/10" /></div>
        )}
        {photosCount > 1 && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1">
            <Camera size={10} /> {photosCount}
          </div>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-white font-bold text-lg truncate">{v.title || "(sans titre)"}</h3>
              <StatusPill status={v.status} />
            </div>
            {v.price && <div className="text-[#FF3B30] font-bold">{v.price}</div>}
            {(v.owner_first_name || v.owner_last_name) && (
              <div className="text-white/40 text-xs mt-1 flex items-center gap-1">
                <User size={11} /> {[v.owner_first_name, v.owner_last_name].filter(Boolean).join(" ")}
                {v.owner_phone ? <span className="text-white/30"> · {v.owner_phone}</span> : null}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={`/depot-vente/fiche/${v.id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`dv-tech-pdf-${v.id}`}
              className="text-white/60 hover:text-[#FF3B30] p-2 rounded-md hover:bg-white/[0.06]"
              title="Fiche PDF imprimable"
            >
              <FileText size={15} />
            </a>
            <button
              onClick={onEdit}
              data-testid={`dv-tech-edit-${v.id}`}
              className="text-white/60 hover:text-white p-2 rounded-md hover:bg-white/[0.06]"
              title="Modifier"
            >
              <Edit size={15} />
            </button>
            <button
              onClick={onDelete}
              data-testid={`dv-tech-delete-${v.id}`}
              className="text-white/60 hover:text-red-400 p-2 rounded-md hover:bg-red-500/[0.06]"
              title="Supprimer"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-sm text-white/70 mb-3">
          {v.year && <div className="flex items-center gap-1.5"><Calendar size={12} className="text-[#FF3B30]" /> {v.year}</div>}
          {v.km && <div className="flex items-center gap-1.5"><Gauge size={12} className="text-[#FF3B30]" /> {v.km}</div>}
          {v.fuel && <div className="flex items-center gap-1.5"><Fuel size={12} className="text-[#FF3B30]" /> {v.fuel}</div>}
          {v.gearbox && <div className="flex items-center gap-1.5"><Cog size={12} className="text-[#FF3B30]" /> {v.gearbox}</div>}
        </div>

        {v.vin && <div className="text-white/40 text-[11px] font-mono">VIN {v.vin}</div>}

        {(v.highlights && v.highlights.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {v.highlights.slice(0, 5).map((h, i) => (
              <span key={i} className="text-xs text-white/60 bg-white/[0.04] border border-white/[0.06] px-2 py-0.5 rounded">
                {h}
              </span>
            ))}
            {v.highlights.length > 5 && (
              <span className="text-xs text-white/40">+{v.highlights.length - 5}</span>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default DepotVenteTech;
